'use client';

import type { ReactNode } from 'react';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import type { FinderData } from '@/lib/types';
import { countActive, type FilterState } from '@/lib/filters';
import { RemovableChip } from './chips';

interface FilterBarProps {
  options: FinderData['options'];
  filters: FilterState;
  onChange: (next: FilterState) => void;
  onOpenFilters: () => void;
  /** The campus dropdown, rendered beside the search box */
  campusControl?: ReactNode;
  hideSearch?: boolean;
}

/**
 * Campus + search box + Filters button, with the applied filters shown as
 * removable chips underneath so nobody has to open the sheet to see why the
 * list is short.
 */
export function FilterBar({
  options,
  filters,
  onChange,
  onOpenFilters,
  campusControl,
  hideSearch = false,
}: FilterBarProps) {
  const active = countActive(filters);
  const label = (list: { id: number; name: string }[], id: number | null) =>
    list.find((o) => o.id === id)?.name ?? String(id);

  const chips: { key: string; text: string; remove: () => void }[] = [];
  if (filters.ministry !== null)
    chips.push({
      key: 'ministry',
      text: label(options.ministries, filters.ministry),
      remove: () => onChange({ ...filters, ministry: null, program: null }),
    });
  if (filters.program !== null)
    chips.push({
      key: 'program',
      text: label(options.programs, filters.program),
      remove: () => onChange({ ...filters, program: null }),
    });
  if (filters.type !== null)
    chips.push({
      key: 'type',
      text: label(options.types, filters.type),
      remove: () => onChange({ ...filters, type: null }),
    });
  if (filters.frequency !== 0)
    chips.push({
      key: 'frequency',
      text: filters.frequency === 1 ? 'Ongoing' : 'One time',
      remove: () => onChange({ ...filters, frequency: 0 }),
    });
  if (filters.gender !== null)
    chips.push({
      key: 'gender',
      text:
        filters.gender === 2
          ? 'Open to women'
          : filters.gender === 1
            ? 'Open to men'
            : label(options.genders, filters.gender),
      remove: () => onChange({ ...filters, gender: null }),
    });
  if (filters.age !== null)
    chips.push({
      key: 'age',
      text: `Age ${filters.age}`,
      remove: () => onChange({ ...filters, age: null }),
    });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        {campusControl && <div className="sm:shrink-0">{campusControl}</div>}
        <div className="flex flex-1 items-center gap-2">
          {!hideSearch && (
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute top-1/2 left-4 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="search"
                value={filters.search}
                onChange={(e) => onChange({ ...filters, search: e.target.value })}
                placeholder="Search teams, ministries, or roles"
                aria-label="Search serve opportunities"
                className="w-full rounded-full border border-gray-200 bg-white py-3 pr-10 pl-11 text-sm text-[#1c2b39] shadow-sm placeholder:text-gray-400 focus:border-[#299c8f] focus:ring-2 focus:ring-[#299c8f]/30 focus:outline-none"
              />
              {filters.search && (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => onChange({ ...filters, search: '' })}
                  className="absolute top-1/2 right-2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          )}
          <button
            type="button"
            onClick={onOpenFilters}
            className={`relative inline-flex shrink-0 items-center gap-2 rounded-full bg-[#1c2b39] px-5 py-3 text-sm font-bold text-white shadow-sm transition-all hover:scale-105 hover:shadow-md active:scale-95 ${hideSearch ? 'flex-1 justify-center sm:flex-none' : ''}`}
          >
            <SlidersHorizontal className="h-4 w-4" strokeWidth={2.5} />
            <span className={hideSearch ? '' : 'hidden sm:inline'}>Filters</span>
            {active > 0 && (
              <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-[#299c8f] px-1.5 text-xs font-bold text-white">
                {active}
              </span>
            )}
          </button>
        </div>
      </div>

      {chips.length > 0 && (
        <div className="scrollbar-hide -mx-1 flex items-center gap-2 overflow-x-auto px-1 py-0.5">
          {chips.map((c) => (
            <RemovableChip key={c.key} onRemove={c.remove}>
              {c.text}
            </RemovableChip>
          ))}
          <button
            type="button"
            onClick={() =>
              onChange({
                ...filters,
                ministry: null,
                program: null,
                type: null,
                frequency: 0,
                gender: null,
                age: null,
              })
            }
            className="shrink-0 px-2 text-xs font-semibold text-gray-500 hover:text-[#1c2b39]"
          >
            Clear all
          </button>
        </div>
      )}
    </div>
  );
}
